import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'framer-motion';
import {
  MapPin,
  Truck,
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  TrendingDown,
  Clock,
  Leaf,
  Star,
  Loader2,
  Ship,
  Plane,
  Train,
  Container,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { PageHeader } from '@/components/common';
import { toast } from 'sonner';
import { stagger, fadeUp } from '@/lib/animations';

// Mode-specific configurations
const MODE_CONFIG = {
  Ocean: {
    locationLabel: 'Port',
    locationPlaceholder: 'Shanghai Port',
    locationIcon: Ship,
    weightUnit: 'kg',
    volumeUnit: 'cbm',
    additionalFields: ['containerType', 'containerCount'],
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
  },
  Air: {
    locationLabel: 'Airport',
    locationPlaceholder: 'JFK International',
    locationIcon: Plane,
    weightUnit: 'kg',
    volumeUnit: 'cbm',
    additionalFields: ['uldType'],
    color: 'text-sky-600',
    bgColor: 'bg-sky-50',
  },
  TruckFTL: {
    locationLabel: 'Address',
    locationPlaceholder: 'Houston Distribution Center',
    locationIcon: Truck,
    weightUnit: 'lbs',
    volumeUnit: 'cu ft',
    additionalFields: ['truckType'],
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
  },
  TruckLTL: {
    locationLabel: 'Address',
    locationPlaceholder: 'Houston Distribution Center',
    locationIcon: Truck,
    weightUnit: 'lbs',
    volumeUnit: 'cu ft',
    additionalFields: ['palletCount'],
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
  },
  Rail: {
    locationLabel: 'Terminal',
    locationPlaceholder: 'Union Pacific Terminal',
    locationIcon: Train,
    weightUnit: 'lbs',
    volumeUnit: 'cu ft',
    additionalFields: ['railcarType'],
    color: 'text-gray-600',
    bgColor: 'bg-gray-50',
  },
  Intermodal: {
    locationLabel: 'Location',
    locationPlaceholder: 'Houston Intermodal Facility',
    locationIcon: Container,
    weightUnit: 'kg',
    volumeUnit: 'cbm',
    additionalFields: ['containerType', 'containerCount'],
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
  },
};

// Shipment form schema
const shipmentSchema = z.object({
  orderNumber: z.string().optional(),
  shipmentType: z.string().min(1, 'Shipment type is required'),

  // Origin
  originCity: z.string().min(1, 'Origin location is required'),
  originState: z.string().min(1, 'Origin state/region is required'),
  originCountry: z.string().min(1, 'Origin country is required'),
  originPostal: z.string().optional(),

  // Destination
  destCity: z.string().min(1, 'Destination location is required'),
  destState: z.string().min(1, 'Destination state/region is required'),
  destCountry: z.string().min(1, 'Destination country is required'),
  destPostal: z.string().optional(),

  // Cargo Details
  totalWeight: z.number().min(0.01, 'Weight must be greater than 0'),
  totalVolume: z.number().min(0.01, 'Volume must be greater than 0'),
  pieceCount: z.number().int().min(1, 'At least 1 piece required'),
  requiresHazmat: z.boolean().default(false),
  requiresTempControl: z.boolean().default(false),
  specialInstructions: z.string().optional(),

  // Mode-specific fields
  containerType: z.string().optional(),
  containerCount: z.number().optional(),
  uldType: z.string().optional(),
  truckType: z.string().optional(),
  palletCount: z.number().optional(),
  railcarType: z.string().optional(),

  // Schedule
  pickupDate: z.string().min(1, 'Pickup date is required'),
  deliveryDate: z.string().min(1, 'Delivery date is required'),
});

type ShipmentFormData = z.infer<typeof shipmentSchema>;

interface RouteOption {
  id: string;
  optionNumber: number;
  totalCost: number;
  totalDistance: number;
  totalTransitDays: number;
  carbonEmissions?: number;
  isRecommended: boolean;
  costScore?: number;
  timeScore?: number;
  reliabilityScore?: number;
  legs: RouteLeg[];
}

interface RouteLeg {
  legNumber: number;
  mode: string;
  carrierName: string;
  originCity: string;
  originState: string;
  destCity: string;
  destState: string;
  distance: number;
  transitDays: number;
  cost: number;
}

const MODE_ICONS: Record<string, string> = {
  Ocean: '🚢',
  Air: '✈️',
  TruckFTL: '🚚',
  TruckLTL: '📦',
  Rail: '🚂',
  Intermodal: '📦',
};

export default function ShipmentPlanner() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [routeOptions, setRouteOptions] = useState<RouteOption[]>([]);
  const [selectedRoutes, setSelectedRoutes] = useState<string[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<ShipmentFormData>({
    resolver: zodResolver(shipmentSchema),
    defaultValues: {
      shipmentType: 'TruckFTL',
      requiresHazmat: false,
      requiresTempControl: false,
      originCountry: 'USA',
      destCountry: 'USA',
    },
  });

  const formData = watch();
  const selectedMode = formData.shipmentType as keyof typeof MODE_CONFIG;
  const modeConfig = MODE_CONFIG[selectedMode] || MODE_CONFIG.TruckFTL;

  const onSubmit = async (data: ShipmentFormData) => {
    if (currentStep === 1) {
      // Generate route options
      setIsGenerating(true);
      try {
        // Simulate API call
        await new Promise((resolve) => setTimeout(resolve, 2000));

        // Mock route options
        const mockRoutes: RouteOption[] = [
          {
            id: '1',
            optionNumber: 1,
            totalCost: 2850,
            totalDistance: 1250,
            totalTransitDays: 3,
            carbonEmissions: 450,
            isRecommended: true,
            costScore: 85,
            timeScore: 90,
            reliabilityScore: 95,
            legs: [
              {
                legNumber: 1,
                mode: data.shipmentType,
                carrierName: 'Premium Logistics',
                originCity: data.originCity,
                originState: data.originState,
                destCity: data.destCity,
                destState: data.destState,
                distance: 1250,
                transitDays: 3,
                cost: 2850,
              },
            ],
          },
          {
            id: '2',
            optionNumber: 2,
            totalCost: 2450,
            totalDistance: 1280,
            totalTransitDays: 4,
            carbonEmissions: 420,
            isRecommended: false,
            costScore: 95,
            timeScore: 75,
            reliabilityScore: 85,
            legs: [
              {
                legNumber: 1,
                mode: data.shipmentType,
                carrierName: 'Economy Freight',
                originCity: data.originCity,
                originState: data.originState,
                destCity: data.destCity,
                destState: data.destState,
                distance: 1280,
                transitDays: 4,
                cost: 2450,
              },
            ],
          },
          {
            id: '3',
            optionNumber: 3,
            totalCost: 3200,
            totalDistance: 1240,
            totalTransitDays: 2,
            carbonEmissions: 480,
            isRecommended: false,
            costScore: 70,
            timeScore: 100,
            reliabilityScore: 98,
            legs: [
              {
                legNumber: 1,
                mode: data.shipmentType,
                carrierName: 'Express Carriers',
                originCity: data.originCity,
                originState: data.originState,
                destCity: data.destCity,
                destState: data.destState,
                distance: 1240,
                transitDays: 2,
                cost: 3200,
              },
            ],
          },
        ];

        setRouteOptions(mockRoutes);
        setCurrentStep(2);
        toast.success('Generated 3 route options');
      } catch (error) {
        toast.error('Failed to generate routes');
      } finally {
        setIsGenerating(false);
      }
    } else if (currentStep === 2) {
      // Compare routes
      if (selectedRoutes.length < 2) {
        toast.error('Please select at least 2 routes to compare');
        return;
      }
      setCurrentStep(3);
    } else if (currentStep === 3) {
      // Book shipment
      if (selectedRoutes.length !== 1) {
        toast.error('Please select exactly 1 route to book');
        return;
      }
      toast.success('Shipment booked successfully!');
      navigate('/shipments');
    }
  };

  const toggleRouteSelection = (routeId: string) => {
    setSelectedRoutes((prev) =>
      prev.includes(routeId) ? prev.filter((id) => id !== routeId) : [...prev, routeId]
    );
  };

  const LocationIcon = modeConfig.locationIcon;

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          title="Shipment Planner"
          description="Outstanding orders grouped into planned shipments across road, sea, air, and rail."
        />
      </motion.div>

      {/* Progress Steps */}
      <motion.div variants={fadeUp}>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              {['Shipment Details', 'Route Options', 'Comparison', 'Booking'].map((step, index) => (
                <div key={step} className="flex items-center">
                  <div
                    className={`flex items-center gap-2 ${
                      index + 1 <= currentStep ? 'text-primary' : 'text-muted-foreground'
                    }`}
                  >
                    {index + 1 < currentStep ? (
                      <CheckCircle2 className="w-6 h-6 text-success-500" />
                    ) : (
                      <div
                        className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-semibold ${
                          index + 1 === currentStep
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {index + 1}
                      </div>
                    )}
                    <span className="hidden sm:inline font-medium">{step}</span>
                  </div>
                  {index < 3 && (
                    <div
                      className={`w-12 md:w-24 h-0.5 mx-2 ${
                        index + 1 < currentStep ? 'bg-success-500' : 'bg-muted'
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Step 1: Shipment Details */}
        {currentStep === 1 && (
          <motion.div variants={fadeUp} className="space-y-6">
            <Card>
              <CardHeader>
                <h3 className="text-base font-medium text-foreground">Basic Information</h3>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="orderNumber">Order Number (Optional)</Label>
                    <Input
                      id="orderNumber"
                      placeholder="AUTO-GENERATED"
                      {...register('orderNumber')}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="shipmentType">Transport Mode *</Label>
                    <Select
                      value={formData.shipmentType}
                      onValueChange={(value) => setValue('shipmentType', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="TruckFTL">🚚 Truck FTL (Full Truckload)</SelectItem>
                        <SelectItem value="TruckLTL">📦 Truck LTL (Less Than Truckload)</SelectItem>
                        <SelectItem value="Ocean">🚢 Ocean Freight</SelectItem>
                        <SelectItem value="Air">✈️ Air Freight</SelectItem>
                        <SelectItem value="Rail">🚂 Rail Transport</SelectItem>
                        <SelectItem value="Intermodal">📦 Intermodal</SelectItem>
                      </SelectContent>
                    </Select>
                    {errors.shipmentType && (
                      <p className="text-sm text-destructive">{errors.shipmentType.message}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <h3 className="text-base font-medium text-foreground">Origin & Destination</h3>
                <p className="text-sm text-muted-foreground">
                  Selected Mode: <span className="font-semibold">{selectedMode}</span> - Enter{' '}
                  <span className="font-semibold">{modeConfig.locationLabel}</span> details
                </p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Origin */}
                  <div className={`space-y-4 p-4 rounded-lg border-2 ${modeConfig.bgColor}`}>
                    <h3 className="text-sm font-semibold flex items-center gap-2">
                      <LocationIcon className={`w-5 h-5 ${modeConfig.color}`} />
                      Origin {modeConfig.locationLabel}
                    </h3>
                    <div className="space-y-2">
                      <Label htmlFor="originCity">{modeConfig.locationLabel} Name / City *</Label>
                      <Input
                        id="originCity"
                        placeholder={modeConfig.locationPlaceholder}
                        {...register('originCity')}
                        className={errors.originCity ? 'border-destructive' : ''}
                      />
                      {errors.originCity && (
                        <p className="text-sm text-destructive">{errors.originCity.message}</p>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="originState">State / Region *</Label>
                        <Input
                          id="originState"
                          placeholder={selectedMode === 'Ocean' ? 'Shanghai' : 'TX'}
                          {...register('originState')}
                          className={errors.originState ? 'border-destructive' : ''}
                        />
                        {errors.originState && (
                          <p className="text-sm text-destructive">{errors.originState.message}</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="originCountry">Country *</Label>
                        <Input id="originCountry" {...register('originCountry')} />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="originPostal">
                        {selectedMode === 'Ocean' || selectedMode === 'Air'
                          ? 'IATA/Port Code'
                          : 'Postal Code'}
                      </Label>
                      <Input
                        id="originPostal"
                        placeholder={
                          selectedMode === 'Ocean'
                            ? 'CNSHA'
                            : selectedMode === 'Air'
                              ? 'JFK'
                              : '77001'
                        }
                        {...register('originPostal')}
                      />
                    </div>
                  </div>

                  {/* Destination */}
                  <div className={`space-y-4 p-4 rounded-lg border-2 ${modeConfig.bgColor}`}>
                    <h3 className="text-sm font-semibold flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-red-500" />
                      Destination {modeConfig.locationLabel}
                    </h3>
                    <div className="space-y-2">
                      <Label htmlFor="destCity">{modeConfig.locationLabel} Name / City *</Label>
                      <Input
                        id="destCity"
                        placeholder={
                          selectedMode === 'Ocean'
                            ? 'Rotterdam Port'
                            : selectedMode === 'Air'
                              ? 'LAX International'
                              : 'Dallas Distribution Center'
                        }
                        {...register('destCity')}
                        className={errors.destCity ? 'border-destructive' : ''}
                      />
                      {errors.destCity && (
                        <p className="text-sm text-destructive">{errors.destCity.message}</p>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="destState">State / Region *</Label>
                        <Input
                          id="destState"
                          placeholder={selectedMode === 'Ocean' ? 'South Holland' : 'CA'}
                          {...register('destState')}
                          className={errors.destState ? 'border-destructive' : ''}
                        />
                        {errors.destState && (
                          <p className="text-sm text-destructive">{errors.destState.message}</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="destCountry">Country *</Label>
                        <Input id="destCountry" {...register('destCountry')} />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="destPostal">
                        {selectedMode === 'Ocean' || selectedMode === 'Air'
                          ? 'IATA/Port Code'
                          : 'Postal Code'}
                      </Label>
                      <Input
                        id="destPostal"
                        placeholder={
                          selectedMode === 'Ocean'
                            ? 'NLRTM'
                            : selectedMode === 'Air'
                              ? 'LAX'
                              : '90001'
                        }
                        {...register('destPostal')}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <h3 className="text-base font-medium text-foreground">Cargo Details</h3>
                <p className="text-sm text-muted-foreground">
                  Units: Weight in <span className="font-semibold">{modeConfig.weightUnit}</span>,
                  Volume in <span className="font-semibold">{modeConfig.volumeUnit}</span>
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="totalWeight">Total Weight ({modeConfig.weightUnit}) *</Label>
                    <Input
                      id="totalWeight"
                      type="number"
                      step="0.01"
                      placeholder={
                        selectedMode === 'Ocean' || selectedMode === 'Air' ? '5000' : '1000'
                      }
                      {...register('totalWeight', { valueAsNumber: true })}
                      className={errors.totalWeight ? 'border-destructive' : ''}
                    />
                    {errors.totalWeight && (
                      <p className="text-sm text-destructive">{errors.totalWeight.message}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="totalVolume">Total Volume ({modeConfig.volumeUnit}) *</Label>
                    <Input
                      id="totalVolume"
                      type="number"
                      step="0.01"
                      placeholder={
                        selectedMode === 'Ocean' || selectedMode === 'Air' ? '25' : '100'
                      }
                      {...register('totalVolume', { valueAsNumber: true })}
                      className={errors.totalVolume ? 'border-destructive' : ''}
                    />
                    {errors.totalVolume && (
                      <p className="text-sm text-destructive">{errors.totalVolume.message}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pieceCount">
                      {selectedMode === 'TruckLTL' ? 'Pallet Count' : 'Piece Count'} *
                    </Label>
                    <Input
                      id="pieceCount"
                      type="number"
                      placeholder="10"
                      {...register('pieceCount', { valueAsNumber: true })}
                      className={errors.pieceCount ? 'border-destructive' : ''}
                    />
                    {errors.pieceCount && (
                      <p className="text-sm text-destructive">{errors.pieceCount.message}</p>
                    )}
                  </div>
                </div>

                {/* Mode-specific fields */}
                {(selectedMode === 'Ocean' || selectedMode === 'Intermodal') && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-blue-50 rounded-lg border">
                    <div className="space-y-2">
                      <Label htmlFor="containerType">Container Type</Label>
                      <Select
                        value={formData.containerType || 'STANDARD_20'}
                        onValueChange={(value) => setValue('containerType', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select container type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="STANDARD_20">20' Standard</SelectItem>
                          <SelectItem value="STANDARD_40">40' Standard</SelectItem>
                          <SelectItem value="HC_40">40' High Cube</SelectItem>
                          <SelectItem value="REEFER_20">20' Reefer</SelectItem>
                          <SelectItem value="REEFER_40">40' Reefer</SelectItem>
                          <SelectItem value="OPEN_TOP_20">20' Open Top</SelectItem>
                          <SelectItem value="FLAT_RACK_40">40' Flat Rack</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="containerCount">Number of Containers</Label>
                      <Input
                        id="containerCount"
                        type="number"
                        placeholder="1"
                        {...register('containerCount', { valueAsNumber: true })}
                      />
                    </div>
                  </div>
                )}

                {selectedMode === 'Air' && (
                  <div className="p-4 bg-sky-50 rounded-lg border">
                    <div className="space-y-2">
                      <Label htmlFor="uldType">ULD Type (Unit Load Device)</Label>
                      <Select
                        value={formData.uldType || 'NONE'}
                        onValueChange={(value) => setValue('uldType', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select ULD type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="NONE">Loose Cargo</SelectItem>
                          <SelectItem value="AKE">AKE - LD3 Container</SelectItem>
                          <SelectItem value="AAP">AAP - LD8 Pallet</SelectItem>
                          <SelectItem value="PMC">PMC - LD6 Container</SelectItem>
                          <SelectItem value="PAG">PAG - LD3 Pallet</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                {selectedMode === 'TruckFTL' && (
                  <div className="p-4 bg-orange-50 rounded-lg border">
                    <div className="space-y-2">
                      <Label htmlFor="truckType">Truck Type</Label>
                      <Select
                        value={formData.truckType || 'DRY_VAN'}
                        onValueChange={(value) => setValue('truckType', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select truck type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="DRY_VAN">Dry Van (53')</SelectItem>
                          <SelectItem value="REEFER">Reefer (Temperature Controlled)</SelectItem>
                          <SelectItem value="FLATBED">Flatbed</SelectItem>
                          <SelectItem value="STEP_DECK">Step Deck</SelectItem>
                          <SelectItem value="BOX_TRUCK">Box Truck</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                {selectedMode === 'Rail' && (
                  <div className="p-4 bg-gray-50 rounded-lg border">
                    <div className="space-y-2">
                      <Label htmlFor="railcarType">Railcar Type</Label>
                      <Select
                        value={formData.railcarType || 'BOX_CAR'}
                        onValueChange={(value) => setValue('railcarType', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select railcar type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="BOX_CAR">Box Car</SelectItem>
                          <SelectItem value="GONDOLA">Gondola</SelectItem>
                          <SelectItem value="HOPPER">Hopper</SelectItem>
                          <SelectItem value="TANK_CAR">Tank Car</SelectItem>
                          <SelectItem value="FLAT_CAR">Flat Car</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-6">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="requiresHazmat"
                      checked={formData.requiresHazmat}
                      onCheckedChange={(checked) => setValue('requiresHazmat', !!checked)}
                    />
                    <Label htmlFor="requiresHazmat" className="font-normal cursor-pointer">
                      🔥 Hazardous Materials (HAZMAT)
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="requiresTempControl"
                      checked={formData.requiresTempControl}
                      onCheckedChange={(checked) => setValue('requiresTempControl', !!checked)}
                    />
                    <Label htmlFor="requiresTempControl" className="font-normal cursor-pointer">
                      ❄️ Temperature Controlled
                    </Label>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="specialInstructions">Special Instructions</Label>
                  <Textarea
                    id="specialInstructions"
                    placeholder={
                      selectedMode === 'Ocean'
                        ? 'e.g., IMO class, booking number, vessel preference...'
                        : selectedMode === 'Air'
                          ? 'e.g., Flight restrictions, customs clearance notes...'
                          : 'e.g., Liftgate required, inside delivery, appointment needed...'
                    }
                    {...register('specialInstructions')}
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <h3 className="text-base font-medium text-foreground">
                  Pickup & Delivery Schedule
                </h3>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="pickupDate">
                      {selectedMode === 'Ocean' ? 'Ready for Departure Date' : 'Pickup Date'} *
                    </Label>
                    <Input
                      id="pickupDate"
                      type="date"
                      {...register('pickupDate')}
                      className={errors.pickupDate ? 'border-destructive' : ''}
                    />
                    {errors.pickupDate && (
                      <p className="text-sm text-destructive">{errors.pickupDate.message}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="deliveryDate">
                      {selectedMode === 'Ocean' ? 'Required Arrival Date' : 'Delivery Date'} *
                    </Label>
                    <Input
                      id="deliveryDate"
                      type="date"
                      {...register('deliveryDate')}
                      className={errors.deliveryDate ? 'border-destructive' : ''}
                    />
                    {errors.deliveryDate && (
                      <p className="text-sm text-destructive">{errors.deliveryDate.message}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end">
              <Button type="submit" size="lg" disabled={isGenerating}>
                {isGenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating Routes...
                  </>
                ) : (
                  <>
                    Generate Route Options
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        )}

        {/* Step 2: Route Options */}
        {currentStep === 2 && (
          <motion.div variants={fadeUp} className="space-y-6">
            <Card>
              <CardHeader>
                <h3 className="text-base font-medium text-foreground">Generated Route Options</h3>
                <p className="text-sm text-muted-foreground">
                  Select routes to compare (select at least 2, or select 1 to book directly)
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                {routeOptions.map((route) => (
                  <div
                    key={route.id}
                    className={`p-4 border rounded-lg cursor-pointer transition-all ${
                      selectedRoutes.includes(route.id)
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50'
                    } ${route.isRecommended ? 'ring-2 ring-success-500/20' : ''}`}
                    onClick={() => toggleRouteSelection(route.id)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <Checkbox checked={selectedRoutes.includes(route.id)} />
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold">Option {route.optionNumber}</h3>
                            {route.isRecommended && (
                              <Badge variant="default" className="bg-success-500">
                                <Star className="w-3 h-3 mr-1" />
                                Recommended
                              </Badge>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <TrendingDown className="w-4 h-4" />
                              <span className="font-semibold text-foreground">
                                ${route.totalCost}
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              <span>{route.totalTransitDays} days</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Truck className="w-4 h-4" />
                              <span>{route.totalDistance} mi</span>
                            </div>
                            {route.carbonEmissions && (
                              <div className="flex items-center gap-1">
                                <Leaf className="w-4 h-4 text-emerald-600" />
                                <span>{route.carbonEmissions} kg CO2</span>
                              </div>
                            )}
                          </div>
                          <div className="text-sm">
                            {route.legs.map((leg) => (
                              <div key={leg.legNumber}>
                                {MODE_ICONS[leg.mode]} {leg.carrierName} - {leg.originCity} →{' '}
                                {leg.destCity}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            <div className="flex justify-between">
              <Button type="button" variant="outline" onClick={() => setCurrentStep(1)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="flex gap-2">
                {selectedRoutes.length === 1 && (
                  <Button type="button" onClick={() => setCurrentStep(4)}>
                    Book Selected Route
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
                {selectedRoutes.length >= 2 && (
                  <Button type="submit">
                    Compare Selected ({selectedRoutes.length})
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* Step 3: Comparison */}
        {currentStep === 3 && (
          <motion.div variants={fadeUp} className="space-y-6">
            <Card>
              <CardHeader>
                <h3 className="text-base font-medium text-foreground">Route Comparison</h3>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-2 text-table-header">Metric</th>
                        {selectedRoutes.map((routeId) => {
                          const route = routeOptions.find((r) => r.id === routeId);
                          return (
                            <th key={routeId} className="p-2 text-table-header">
                              Option {route?.optionNumber}
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b">
                        <td className="p-2 font-medium">Total Cost</td>
                        {selectedRoutes.map((routeId) => {
                          const route = routeOptions.find((r) => r.id === routeId);
                          return (
                            <td key={routeId} className="p-2 text-center">
                              ${route?.totalCost}
                            </td>
                          );
                        })}
                      </tr>
                      <tr className="border-b">
                        <td className="p-2 font-medium">Transit Days</td>
                        {selectedRoutes.map((routeId) => {
                          const route = routeOptions.find((r) => r.id === routeId);
                          return (
                            <td key={routeId} className="p-2 text-center">
                              {route?.totalTransitDays} days
                            </td>
                          );
                        })}
                      </tr>
                      <tr className="border-b">
                        <td className="p-2 font-medium">Carbon Emissions</td>
                        {selectedRoutes.map((routeId) => {
                          const route = routeOptions.find((r) => r.id === routeId);
                          return (
                            <td key={routeId} className="p-2 text-center">
                              {route?.carbonEmissions} kg CO2
                            </td>
                          );
                        })}
                      </tr>
                      <tr>
                        <td className="p-2 font-medium">Action</td>
                        {selectedRoutes.map((routeId) => (
                          <td key={routeId} className="p-2 text-center">
                            <Button
                              size="sm"
                              type="button"
                              onClick={() => {
                                setSelectedRoutes([routeId]);
                                setCurrentStep(4);
                              }}
                            >
                              Select
                            </Button>
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-between">
              <Button type="button" variant="outline" onClick={() => setCurrentStep(2)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Options
              </Button>
            </div>
          </motion.div>
        )}

        {/* Step 4: Booking */}
        {currentStep === 4 && (
          <motion.div variants={fadeUp} className="space-y-6">
            <Card>
              <CardHeader>
                <h3 className="text-base font-medium text-foreground">Confirm Booking</h3>
              </CardHeader>
              <CardContent>
                {selectedRoutes.length === 1 && (
                  <div className="space-y-4">
                    {(() => {
                      const route = routeOptions.find((r) => r.id === selectedRoutes[0]);
                      return (
                        <>
                          <div className="p-4 bg-muted rounded-lg">
                            <h3 className="font-semibold mb-2">Selected Route</h3>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span>Total Cost:</span>
                                <span className="font-semibold">${route?.totalCost}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Transit Time:</span>
                                <span>{route?.totalTransitDays} days</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Distance:</span>
                                <span>{route?.totalDistance} mi</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex justify-between pt-4">
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => setCurrentStep(2)}
                            >
                              <ArrowLeft className="w-4 h-4 mr-2" />
                              Back
                            </Button>
                            <Button type="submit">
                              <CheckCircle2 className="w-4 h-4 mr-2" />
                              Confirm Booking
                            </Button>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}
      </form>
    </motion.main>
  );
}
